import React, { useCallback, useMemo } from "react";
import { Button, Card, Divider, Empty, List, Space, Tag, Typography, message } from "antd";
import useScheduleDraftEditorWorkspace from "../../hooks/useScheduleDraftEditorWorkspace";
import { changeSetAffectsAnalysis } from "../../lib/fieldImpactUtils";

const { Text } = Typography;

function getAffectedFlightCount(changeSet) {
    return new Set((changeSet?.changes || [])
        .map((change) => change?.flightId ?? change?.flight_id)
        .filter((flightId) => flightId !== undefined && flightId !== null)
        .map(String)).size;
}

function formatOperationTime(timestamp) {
    if (!timestamp) {
        return "--";
    }
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
        return "--";
    }
    return date.toLocaleString("zh-CN", { hour12: false });
}

function buildOperationItems(stack = []) {
    return [...stack].reverse().map((changeSet, index) => ({
        changeSet,
        affectedFlightCount: getAffectedFlightCount(changeSet),
        isCurrent: index === 0,
    }));
}

const ScheduleDraftOperationHistoryPanel = () => {
    const {
        data: { localEditState, refetchAnalysis },
    } = useScheduleDraftEditorWorkspace();
    const undoItems = useMemo(() => buildOperationItems(localEditState?.undoStack), [localEditState?.undoStack]);
    const redoItems = useMemo(() => buildOperationItems(localEditState?.redoStack), [localEditState?.redoStack]);
    const hasPendingPreview = Boolean(localEditState?.pendingChangeSet);

    const handleRollback = useCallback((changeSet) => {
        if (!changeSet?.id || hasPendingPreview) {
            if (hasPendingPreview) {
                message.warning("请先确认或取消当前自动计算预览后再回退操作历史");
            }
            return;
        }

        const result = localEditState?.rollbackToChangeSet?.(changeSet.id);
        if (!result) {
            message.warning("未找到可回退的操作");
            return;
        }

        const rolledBackChangeSets = result.rolledBackChangeSets || [];
        if (!rolledBackChangeSets.length) {
            message.info("当前已经位于该操作之后");
            return;
        }

        if (rolledBackChangeSets.some((rolledBackChangeSet) => changeSetAffectsAnalysis(rolledBackChangeSet))) {
            refetchAnalysis?.();
        }
        message.success(`已回退 ${rolledBackChangeSets.length} 个操作`);
    }, [hasPendingPreview, localEditState, refetchAnalysis]);

    const renderUndoItem = ({ changeSet, affectedFlightCount, isCurrent }) => (
        <List.Item
            actions={[
                <Button key="rollback" size="small" disabled={isCurrent || hasPendingPreview} onClick={() => handleRollback(changeSet)}>
                    {isCurrent ? "当前位置" : "回退到此处"}
                </Button>,
            ]}
        >
            <List.Item.Meta
                title={(
                    <Space wrap size={[6, 4]}>
                        <Text strong>{changeSet?.label || changeSet?.type || "未命名操作"}</Text>
                        <Tag>{changeSet?.type || "CHANGE_SET"}</Tag>
                    </Space>
                )}
                description={(
                    <Space wrap size={[8, 4]}>
                        <Text type="secondary">{formatOperationTime(changeSet?.timestamp)}</Text>
                        <Tag color="blue">影响 {affectedFlightCount} 个航班</Tag>
                        <Tag>{(changeSet?.changes || []).length} 项变更</Tag>
                    </Space>
                )}
            />
        </List.Item>
    );

    const renderRedoItem = ({ changeSet, affectedFlightCount }) => (
        <List.Item>
            <List.Item.Meta
                title={(
                    <Space wrap size={[6, 4]}>
                        <Text>{changeSet?.label || changeSet?.type || "未命名操作"}</Text>
                        <Tag color="default">已撤销</Tag>
                    </Space>
                )}
                description={(
                    <Space wrap size={[8, 4]}>
                        <Text type="secondary">{formatOperationTime(changeSet?.timestamp)}</Text>
                        <Tag>影响 {affectedFlightCount} 个航班</Tag>
                    </Space>
                )}
            />
        </List.Item>
    );

    return (
        <Card title="操作历史" className="schedule-draft-side-card" size="small">
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Text type="secondary">仅展示未保存的本地航班编辑历史；保存成功后历史会清空。匹配工作态独立管理。</Text>
                {hasPendingPreview ? <Text type="warning">当前存在自动计算预览，请先确认或取消后再回退历史。</Text> : null}
                {undoItems.length ? (
                    <List
                        size="small"
                        dataSource={undoItems}
                        renderItem={renderUndoItem}
                    />
                ) : (
                    <Empty description="暂无未保存操作" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
                {redoItems.length ? (
                    <>
                        <Divider orientation="left" plain>已撤销，可重做</Divider>
                        <List
                            size="small"
                            dataSource={redoItems}
                            renderItem={renderRedoItem}
                        />
                    </>
                ) : null}
            </Space>
        </Card>
    );
};

export default ScheduleDraftOperationHistoryPanel;
